<template>
  <div class="wrapper">
    <v-stepper v-model="currentStep" vertical class="elevation-0">
      <template v-if="!existingClient">
        <v-stepper-step
          :complete="currentStep > steps.agreement"
          :step="steps.agreement">
          {{ $t('general_agreement') }}
        </v-stepper-step>

        <v-stepper-content :step="steps.agreement">
          <PrivatePersonAgreement v-if="clientType === 'private'"/>
          <CompanyAgreement v-else/>
          <div class="text-xs-center">
            <v-btn class="continue-btn" color="primary" @click="currentStep += 1">{{ $t('agree') }}</v-btn>
          </div>
        </v-stepper-content>
      </template>

      <v-stepper-step
        :complete="currentStep > steps.gdpr"
        :step="steps.gdpr">
        {{ $t('privacy_policy_gdpr') }}
      </v-stepper-step>

      <v-stepper-content :step="steps.gdpr">
        <GDPR/>
        <div class="text-xs-center">
          <v-btn class="continue-btn" color="primary" @click="finishAgreements">{{ $t('agree') }}</v-btn>
        </div>
      </v-stepper-content>
    </v-stepper>

    <div v-if="currentStep === steps.sign && clientType" id="sign-options" class="mb-5 text-xs-center">
      <v-progress-circular
        v-if="requestPending"
        color="primary"
        class="mt-5"
        indeterminate
      />

      <template v-else>
        <DigitalDocumentSign
          @signDocs="signDocs"
        />
        <SendDocumentsToManager v-if="clientType === 'company' && clientRole === 'authorized_person'"/>
        <div class="manual-sign-div">
          {{ $t('unable_to_sign_digitally') }}.
          {{ $t('we_answer_questions_via_email') }}:
          <a href="mailto:info.lt@cramo.com">info.lt@cramo.com</a>
          {{ $t('or_via_phone') }}:
          <a href="tel:+37060037350">+370 600 37350</a>
        </div>
      </template>
    </div>

    <BaseDialog :is-open="signDialog" persistent>
      <div v-if="!isSigningExpired" class="position-relative">
        <div v-if="isDocumentLocked" class="pa-3 floating-message text-xs-right">
          {{ $t('document_is_locked') }}<br>
          ({{ $t('it_usually_takes_around_5_minutes') }})
        </div>
        <SignDocument
          :dialog="signDialog"
          :signUrl="signUrl"
          :wrapper-classes="'pa-0 iframe-wrapper'"
          :checkingSignature="checkingSignature"
          @cancel="closeSignDialog"
        >
          <span v-if="signUrl" class="pl-2">
            {{ $t('time_left_to_sign') }}: {{ timerLabel }}
          </span>
        </SignDocument>
      </div>
      <div v-else class="pa-3 text-xs-center white">
        <h2 class="subheading">
          {{ $t('time_to_sign_expiration_message') }}
        </h2>
        <v-btn
          color="primary"
          class="mt-3"
          @click="getDocumentSignTokens"
        >
          {{ $t('initiate_signing') }}
        </v-btn>
      </div>
    </BaseDialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import DigitalDocumentSign from '../components/DigitalDocumentSign.vue';
import SendDocumentsToManager from '../components/SendDocumentsToManager.vue';
import SignDocument from '../components/SignDocument.vue';
import GDPR from '../components/GDPR.vue';
import PrivatePersonAgreement from '../components/PrivatePersonAgreement.vue';
import CompanyAgreement from '../components/CompanyAgreement.vue';
import BaseDialog from '../components/BaseDialog.vue';

export default {
  name: 'Agreements',

  components: {
    BaseDialog,
    DigitalDocumentSign,
    SendDocumentsToManager,
    SignDocument,
    GDPR,
    PrivatePersonAgreement,
    CompanyAgreement,
  },

  data() {
    return {
      secondsLeft: null,
      timerLabel: '',

      displaySigningOptions: false,
      currentStep: 1,
      signDialog: false,
      checkingSignature: false,
      isDocumentLocked: false,
      isSigningExpired: false,
      signUrl: '',
      signingAccessToken: '',
      signingSignerToken: '',
      generatedDocument: {},
      signingMethod: '',
      steps: {
        agreement: 1,
        gdpr: 2,
        sign: 3,
      },
      requestPending: false,
      timerId: null,
    };
  },

  computed: {
    ...mapState({
      existingClient: state => state.clients.isExisting,
      clientType: state => state.clients.type,
      clientRole: state => (state.clients.companyFormItem && state.clients.companyFormItem.contacts && state.clients.companyFormItem.contacts.length
        ? state.clients.companyFormItem.contacts[0].type // manager, authorized_person, other
        : null),
    }),
  },

  created() {
    if (this.existingClient) {
      this.steps.gdpr = 1;
      this.steps.sign = 2;
    }
  },

  mounted() {
    this.$vuetify.goTo(0, {
      duration: 900,
      easing: 'easeOutQuint',
    });
  },

  methods: {
    ...mapActions({
      createPrivateClient: 'clients/createPrivate',
      createCompanyClient: 'clients/createCompany',
      getSigningTokens: 'documents/getSigningTokens',
      uploadDocuments: 'documents/upload',
      uploadWarrant: 'documents/uploadWarrant',
      checkDocumentSignature: 'documents/checkSignature',
    }),

    ...mapMutations({
      resetCompany: 'clients/storeCompany',
      resetPrivate: 'clients/storePrivate',
    }),

    setTimer() {
      this.secondsLeft = 300;
      const setTimerLabel = () => {
        this.secondsLeft -= 1;
        let label = `0${Math.floor(this.secondsLeft / 60)}:`;
        const seconds = this.secondsLeft % 60;
        if (seconds < 10) {
          label += '0';
        }
        label += seconds;
        this.timerLabel = label;
      };
      setTimerLabel();
      const intervalId = setInterval(() => {
        if (this.secondsLeft <= 0) {
          clearInterval(intervalId);
          this.isSigningExpired = true;
          this.signingAccessToken = '';
          this.signingSignerToken = '';
        } else {
          setTimerLabel();
        }
      }, 1000);
    },

    signDocs(signingMethod) {
      this.signingMethod = signingMethod;
      this.getDocumentSignTokens();
      this.signDialog = true;
    },

    finishAgreements() {
      this.requestPending = true;
      this.currentStep = this.steps.sign;
      if (this.clientType) {
        setTimeout(() => {
          this.$vuetify.goTo('#sign-options', {
            duration: 900,
            easing: 'easeOutQuint',
          });
        }, 500);
        this.createClient();
      } else {
        this.uploadDocuments().then(() => {
          this.$router.replace({ name: 'completed' });
        }).catch(() => {
          this.$router.replace({ name: 'error' });
        }).finally(() => {
          this.requestPending = false;
        });
      }
    },

    createClient() {
      if (this.clientType === 'company') {
        this.createCompanyClient().then((res) => {
          if (this.clientRole === 'authorized_person') {
            this.uploadWarrant(res.data.id);
          }
          this.generatedDocument = res.data.contract;
          // this.resetCompany();
        }).catch(() => {
          this.$router.replace({ name: 'company-form' });
        }).finally(() => {
          this.requestPending = false;
        });
      } else if (this.clientType === 'private') {
        this.createPrivateClient().then((res) => {
          // this.resetPrivate();
          this.generatedDocument = res.data.contract;
        }).catch(() => {
          this.$router.replace({ name: 'private-person-form' });
        }).finally(() => {
          this.requestPending = false;
        });
      }
    },

    getDocumentSignTokens() {
      this.isSigningExpired = false;
      this.signUrl = '';
      if (this.signingAccessToken && this.signingSignerToken) {
        this.signUrl = `${process.env.VUE_APP_DOKOBIT_SIGNING_URL}/signing/${this.signingAccessToken}?access_token=${this.signingSignerToken}&signing_option=${this.signingMethod}`;
        this.signDialog = true;
        return;
      }
      this.getSigningTokens(this.generatedDocument).then((res) => {
        this.isDocumentLocked = false;
        this.setTimer();
        this.signingAccessToken = res.data.token;
        this.signingSignerToken = res.data.signer_token;
        this.signUrl = `${process.env.VUE_APP_DOKOBIT_SIGNING_URL}/signing/${this.signingAccessToken}?access_token=${this.signingSignerToken}&signing_option=${this.signingMethod}`;
        this.signDialog = true;
      }).catch((err) => {
        if (err.response && err.response.status === 409) {
          // Document is currently being signed by someone else.
          // Wait 5 seconds, then try to fetch signing tokens again.
          this.isDocumentLocked = true;
          this.signingAccessToken = '';
          this.signingSignerToken = '';
          this.timerId = setTimeout(() => {
            this.getDocumentSignTokens();
          }, 5000);
        }
      });
    },

    closeSignDialog() {
      clearTimeout(this.timerId);
      this.checkingSignature = true;
      this.isDocumentLocked = false;
      this.signUrl = '';
      setTimeout(() => {
        this.checkDocumentSignature(this.generatedDocument).then((res) => {
          if (res.data.status) {
            this.$router.replace({ name: 'completed' });
          }
          this.checkingSignature = false;
          this.signDialog = false;
        });
      }, 3000);
    },
  },
};
</script>

<style scoped>
  .wrapper {
    padding: 0 36px;
  }

  .continue-btn {
    min-width: 120px;
  }

  .manual-sign-div {
    font-size: 20px;
    font-weight: 400;
    padding-bottom: 64px;
  }

  .form-wrapper {
    background: #F3F3F3;
    padding: 12px 186px;
  }

  @media screen and (max-width: 700px) {
    .form-wrapper {
      padding: 0 24px;
    }
  }
</style>
